/**
 * The Hebrew calendar, from the published arithmetic.
 *
 * Implemented from Reingold and Dershowitz, *Calendrical Calculations: The Ultimate
 * Edition* (Cambridge University Press, 2018), chapter 8, which in turn formalises the
 * rules fixed by Hillel II in the fourth century CE and set out by Maimonides:
 *
 * - A year has 12 months, or 13 in a leap year; years 3, 6, 8, 11, 14, 17 and 19 of the
 *   19-year cycle are leap (`(7y + 1) mod 19 < 7`).
 * - A month starts at the *molad*, the mean new moon, of 29 days 12 hours 793 parts (a part
 *   is 1/1080 hour). The year starts on 1 Tishri, which is the molad's day moved by the
 *   four postponement rules: Rosh Hashanah never falls on Sunday, Wednesday or Friday
 *   (*lo ADU rosh*), the molad-zaken rule (molad at or after noon), and the two rules that
 *   stop a year from being too short (*GaTaRaD*, *BeTU'TeKaPoT*).
 * - Because of those postponements a year has 353, 354 or 355 days (385 in a leap year):
 *   deficient, regular or complete.
 *
 * Month numbers follow the book: Nisan is 1 (the biblical first month), Tishri is 7 (the
 * civil new year) and Adar is 12, with Adar II as 13 in leap years. In a leap year the
 * 30-day month is Adar I; in an ordinary year Adar has 29 days.
 *
 * The Hebrew day begins at sunset, which this arithmetic cannot know: a date here is the
 * date in force during the *daytime* of a civil day. For the evening after sunset, add one
 * day.
 */

import { mod } from "@/lib/tool-kit/calendars/rd";

/** R.D. of 1 Tishri AM 1: Julian 7 October 3761 BCE. */
export const HEBREW_EPOCH = -1_373_427;

export const NISAN = 1;
export const SIVAN = 3;
export const TISHRI = 7;
export const MARHESHVAN = 8;
export const KISLEV = 9;
export const ADAR = 12;
export const ADAR_II = 13;

export interface HebrewDate {
  year: number;
  /** 1 = Nisan … 7 = Tishri … 12 = Adar (or Adar I), 13 = Adar II. */
  month: number;
  day: number;
}

export function isHebrewLeapYear(year: number): boolean {
  return mod(7 * year + 1, 19) < 7;
}

export function lastMonthOfHebrewYear(year: number): number {
  return isHebrewLeapYear(year) ? 13 : 12;
}

/** Days from the epoch to the molad-based new year, before the length correction. */
export function hebrewCalendarElapsedDays(year: number): number {
  const monthsElapsed = Math.floor((235 * year - 234) / 19);
  const partsElapsed = 12_084 + 13_753 * monthsElapsed;
  const days = 29 * monthsElapsed + Math.floor(partsElapsed / 25_920);
  return mod(3 * (days + 1), 7) < 3 ? days + 1 : days;
}

/** The delay that keeps neighbouring years from being too short or too long. */
function hebrewYearLengthCorrection(year: number): number {
  const previous = hebrewCalendarElapsedDays(year - 1);
  const current = hebrewCalendarElapsedDays(year);
  const next = hebrewCalendarElapsedDays(year + 1);
  if (next - current === 356) {
    return 2;
  }
  if (current - previous === 382) {
    return 1;
  }
  return 0;
}

/** R.D. of 1 Tishri of a Hebrew year. */
export function hebrewNewYear(year: number): number {
  return (
    HEBREW_EPOCH +
    hebrewCalendarElapsedDays(year) +
    hebrewYearLengthCorrection(year)
  );
}

export function daysInHebrewYear(year: number): number {
  return hebrewNewYear(year + 1) - hebrewNewYear(year);
}

export function isLongMarheshvan(year: number): boolean {
  return [355, 385].includes(daysInHebrewYear(year));
}

export function isShortKislev(year: number): boolean {
  return [353, 383].includes(daysInHebrewYear(year));
}

export type HebrewYearType = "deficient" | "regular" | "complete";

export function hebrewYearType(year: number): HebrewYearType {
  const length = daysInHebrewYear(year) % 10;
  if (length === 3) {
    return "deficient";
  }
  return length === 4 ? "regular" : "complete";
}

const TWENTY_NINE_DAY_MONTHS = new Set([2, 4, 6, 10, 13]);

export function lastDayOfHebrewMonth(year: number, month: number): number {
  if (TWENTY_NINE_DAY_MONTHS.has(month)) {
    return 29;
  }
  if (month === ADAR && !isHebrewLeapYear(year)) {
    return 29;
  }
  if (month === MARHESHVAN && !isLongMarheshvan(year)) {
    return 29;
  }
  if (month === KISLEV && isShortKislev(year)) {
    return 29;
  }
  return 30;
}

/** R.D. of a Hebrew date. The day is not range-checked: use `isValidHebrewDate`. */
export function fixedFromHebrew(date: HebrewDate): number {
  const { year, month, day } = date;
  let rd = hebrewNewYear(year) + day - 1;
  if (month < TISHRI) {
    for (let m = TISHRI; m <= lastMonthOfHebrewYear(year); m++) {
      rd += lastDayOfHebrewMonth(year, m);
    }
    for (let m = NISAN; m < month; m++) {
      rd += lastDayOfHebrewMonth(year, m);
    }
  } else {
    for (let m = TISHRI; m < month; m++) {
      rd += lastDayOfHebrewMonth(year, m);
    }
  }
  return rd;
}

/** The Hebrew date in force during the daytime of an R.D. */
export function hebrewFromFixed(rd: number): HebrewDate {
  const approximate =
    Math.floor((rd - HEBREW_EPOCH) / (35_975_351 / 98_496)) + 1;
  let year = approximate - 1;
  while (hebrewNewYear(year + 1) <= rd) {
    year++;
  }
  while (hebrewNewYear(year) > rd) {
    year--;
  }
  const start =
    rd < fixedFromHebrew({ year, month: NISAN, day: 1 }) ? TISHRI : NISAN;
  let month = start;
  while (
    rd >
    fixedFromHebrew({
      year,
      month,
      day: lastDayOfHebrewMonth(year, month),
    })
  ) {
    month++;
  }
  const day = rd - fixedFromHebrew({ year, month, day: 1 }) + 1;
  return { year, month, day };
}

/** Whether a month number exists in that year and the day exists in that month. */
export function isValidHebrewDate(date: HebrewDate): boolean {
  const { year, month, day } = date;
  if (
    !(
      Number.isInteger(year) &&
      Number.isInteger(month) &&
      Number.isInteger(day)
    ) ||
    year < 1 ||
    month < 1 ||
    month > lastMonthOfHebrewYear(year) ||
    day < 1
  ) {
    return false;
  }
  return day <= lastDayOfHebrewMonth(year, month);
}

/** Index into a twelve-plus-two list of names: Adar, Adar I and Adar II are distinct. */
export type HebrewMonthName =
  | "nisan"
  | "iyyar"
  | "sivan"
  | "tammuz"
  | "av"
  | "elul"
  | "tishri"
  | "marheshvan"
  | "kislev"
  | "tevet"
  | "shevat"
  | "adar"
  | "adarI"
  | "adarII";

const MONTH_NAMES: readonly HebrewMonthName[] = [
  "nisan",
  "iyyar",
  "sivan",
  "tammuz",
  "av",
  "elul",
  "tishri",
  "marheshvan",
  "kislev",
  "tevet",
  "shevat",
  "adar",
];

/** Which name a month carries: month 12 is "Adar I" in a leap year and "Adar" otherwise. */
export function hebrewMonthName(year: number, month: number): HebrewMonthName {
  if (month === ADAR_II) {
    return "adarII";
  }
  if (month === ADAR && isHebrewLeapYear(year)) {
    return "adarI";
  }
  return MONTH_NAMES[month - 1] ?? "nisan";
}

export interface HebrewMonthInfo {
  month: number;
  name: HebrewMonthName;
  /** R.D. of the month's first day (daytime). */
  start: number;
  length: number;
}

/** The months of a Hebrew year in calendar order, Tishri first. */
export function hebrewMonthsOfYear(year: number): HebrewMonthInfo[] {
  const order: number[] = [];
  for (let m = TISHRI; m <= lastMonthOfHebrewYear(year); m++) {
    order.push(m);
  }
  for (let m = NISAN; m < TISHRI; m++) {
    order.push(m);
  }
  return order.map((month) => ({
    month,
    name: hebrewMonthName(year, month),
    start: fixedFromHebrew({ year, month, day: 1 }),
    length: lastDayOfHebrewMonth(year, month),
  }));
}

export type HebrewHoliday =
  | "roshHashanah"
  | "yomKippur"
  | "sukkot"
  | "hanukkah"
  | "purim"
  | "passover"
  | "shavuot";

export interface HolidayDate {
  holiday: HebrewHoliday;
  /** R.D. of the first full (daytime) day. The holiday begins at sunset the day before. */
  day: number;
}

/**
 * The fixed-date holidays of the Hebrew year that *begins* on 1 Tishri of `year`, in
 * calendar order. Purim is 14 Adar (Adar II in a leap year); Hanukkah's first full day is
 * 25 Kislev. The dates are the Diaspora first days; each holiday begins at the sunset
 * before.
 */
export function hebrewHolidays(year: number): HolidayDate[] {
  const at = (month: number, day: number) =>
    fixedFromHebrew({ year, month, day });
  return [
    { holiday: "roshHashanah", day: at(TISHRI, 1) },
    { holiday: "yomKippur", day: at(TISHRI, 10) },
    { holiday: "sukkot", day: at(TISHRI, 15) },
    { holiday: "hanukkah", day: at(KISLEV, 25) },
    {
      holiday: "purim",
      day: at(lastMonthOfHebrewYear(year) === 13 ? ADAR_II : ADAR, 14),
    },
    { holiday: "passover", day: at(NISAN, 15) },
    { holiday: "shavuot", day: at(SIVAN, 6) },
  ];
}
